import React, { useEffect, useMemo, useRef, useState } from "react";

const STAGE_DURATION_MS = 3 * 60 * 1000;

const STAGES = [
  { id: "preparation", label: "Подготовка" },
  { id: "summary", label: "Резюме проекта", sectionKey: "summary" },
  {
    id: "description",
    label: "Описание проекта/продукта/услуги",
    sectionKey: "description",
  },
  { id: "marketAnalysis", label: "Маркетинговый анализ", sectionKey: "marketAnalysis" },
  { id: "productionPlan", label: "Производственный план", sectionKey: "productionPlan" },
  { id: "marketingPlan", label: "Маркетинговый план", sectionKey: "marketingPlan" },
  {
    id: "financialPlan",
    label: "Финансовые показатели и прогнозы",
    sectionKey: "financialPlan",
  },
  { id: "risks", label: "Анализ рисков", sectionKey: "risks" },
  { id: "socialImpact", label: "Социальное влияние", sectionKey: "socialImpact" },
  { id: "finalizing", label: "Финальная обработка" },
];

const SECTION_SEQUENCE = STAGES.filter((stage) => stage.sectionKey).map((stage) => stage.sectionKey);

const deriveStatusStage = (sectionStatusMap = {}, planStatus) => {
  const hasSectionData = SECTION_SEQUENCE.some((key) => {
    const value = sectionStatusMap[key];
    return value && value !== "idle";
  });

  if (!hasSectionData) {
    return 0;
  }

  if (sectionStatusMap.socialImpact === "done" && planStatus !== "DONE") {
    return STAGES.length - 1;
  }

  for (let i = 0; i < SECTION_SEQUENCE.length; i += 1) {
    const key = SECTION_SEQUENCE[i];
    if (sectionStatusMap[key] === "in_progress") {
      return i + 1;
    }
  }

  for (let i = SECTION_SEQUENCE.length - 1; i >= 0; i -= 1) {
    const key = SECTION_SEQUENCE[i];
    if (sectionStatusMap[key] === "done") {
      return i + 1;
    }
  }

  return 0;
};

const getContainerClasses = (variant) => {
  if (variant === "plain") {
    return "w-full";
  }
  return "w-full rounded-xl border border-slate-200 bg-white p-3";
};

const clampStage = (value, total) => Math.min(total - 1, Math.max(0, value));

export default function ProgressMenu({
  status,
  sectionStatusMap = {},
  planUuid,
  className = "",
  variant = "card",
}) {
  const totalStages = STAGES.length;
  const normalizedStatusStage = useMemo(
    () => clampStage(deriveStatusStage(sectionStatusMap, status), totalStages),
    [sectionStatusMap, status, totalStages]
  );

  const shouldShow = status === "PROCESSING";
  const [timerProgress, setTimerProgress] = useState(() => (shouldShow ? normalizedStatusStage : 0));
  const startTimeRef = useRef(null);
  const prevPlanUuidRef = useRef(planUuid);
  const prevStatusRef = useRef(status);

  useEffect(() => {
    if (prevPlanUuidRef.current !== planUuid) {
      prevPlanUuidRef.current = planUuid;
      startTimeRef.current = null;
      setTimerProgress(shouldShow ? normalizedStatusStage : 0);
    }
  }, [planUuid, normalizedStatusStage, shouldShow]);

  useEffect(() => {
    if (prevStatusRef.current !== status) {
      prevStatusRef.current = status;
      if (status === "PROCESSING") {
        startTimeRef.current = null;
        setTimerProgress(normalizedStatusStage);
      } else {
        startTimeRef.current = null;
      }
    }
  }, [status, normalizedStatusStage]);

  useEffect(() => {
    if (!shouldShow) {
      return undefined;
    }

    if (startTimeRef.current === null) {
      startTimeRef.current =
        Date.now() - Math.max(0, normalizedStatusStage) * STAGE_DURATION_MS;
    }

    const tick = () => {
      if (startTimeRef.current === null) {
        return;
      }
      const elapsed = Date.now() - startTimeRef.current;
      const progressFromTime = Math.min(totalStages, elapsed / STAGE_DURATION_MS);
      setTimerProgress((prev) =>
        Math.abs(progressFromTime - prev) > 0.001 ? progressFromTime : prev
      );
    };

    tick();

    if (typeof window === "undefined") {
      return undefined;
    }

    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [shouldShow, normalizedStatusStage, totalStages]);

  useEffect(() => {
    if (!shouldShow) {
      return;
    }
    if (normalizedStatusStage > timerProgress) {
      setTimerProgress(normalizedStatusStage);
      if (startTimeRef.current !== null) {
        startTimeRef.current = Date.now() - normalizedStatusStage * STAGE_DURATION_MS;
      }
    }
  }, [normalizedStatusStage, timerProgress, shouldShow]);

  if (!shouldShow) {
    return null;
  }

  const progressValue = shouldShow
    ? Math.min(totalStages, Math.max(timerProgress, normalizedStatusStage))
    : 0;
  const activeStageIndex = clampStage(
    Math.floor(progressValue >= totalStages ? totalStages - 1 : progressValue),
    totalStages
  );
  const activeStageFraction = Math.min(
    1,
    Math.max(0, progressValue - activeStageIndex)
  );
  const currentStage = STAGES[activeStageIndex] || STAGES[0];
  const completedCount = Math.min(
    totalStages,
    Math.max(1, Math.ceil(progressValue || 0))
  );
  const containerClasses = `${getContainerClasses(variant)}${className ? ` ${className}` : ""}`;

  return (
    <div className={containerClasses}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-slate-900">{currentStage.label}</span>
        <span className="text-xs text-slate-500">{completedCount} из {totalStages}</span>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
        <div className="grid h-full w-full grid-cols-10 gap-[2px] bg-slate-200">
          {STAGES.map((stage, index) => {
            const isDone = index < activeStageIndex;
            const isActive = index === activeStageIndex;
            let fillPercentage = 0;
            if (isDone) {
              fillPercentage = 100;
            } else if (isActive) {
              fillPercentage = activeStageFraction * 100;
            }
            return (
              <div key={stage.id} className="relative h-full overflow-hidden rounded-none bg-slate-100">
                <div
                  className={`absolute inset-y-0 left-0 bg-blue-500 transition-[width] duration-1000 ease-linear ${
                    fillPercentage > 0 ? "opacity-100" : "opacity-0"
                  }`}
                  style={{ width: `${fillPercentage}%` }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
